import type { DataEntity } from '@dvag/dfs-api-client';
import { getCountryList } from '@dvag/dfs-constant-config/get-country-list';
import { useQuery } from '@tanstack/react-query';
import { checkException } from 'config/reactQuery';
import { personApi } from 'service/api/personApi';
import { normalizeCustomerStatus } from 'type/customerStatus';
import { PersonSearchItem, PersonSearchResponseItem } from 'type/person';
import { randomUUID } from 'utils/randomUuid';
import { ConfigCountry, mapCustomerAddress } from './mapCustomerAddress';

const mapSearchPerson = (
  person: PersonSearchResponseItem,
  countryList: ConfigCountry[],
): PersonSearchItem => {
  const address = mapCustomerAddress(person.address, countryList);
  const status = normalizeCustomerStatus(person.status);

  return {
    ...(address ? { address } : {}),
    firstName: person.firstName,
    id: person.id,
    lastName: person.lastName,
    ...(status ? { status } : {}),
  };
};

export const useSearchPerson = (
  searchTerm = '',
  onDataLoaded?: (list: PersonSearchItem[]) => void,
) => {
  const countryList = getCountryList();
  const personService = personApi();

  return useQuery<PersonSearchItem[], DataEntity>({
    queryKey: [`?searchTerm=${searchTerm}`],
    queryFn: async ({ signal }) => {
      if (searchTerm.length === 0) {
        return [];
      }

      const response = await personService.getSearchPerson(searchTerm, { signal });
      const list = (response.data || []).map((person) => mapSearchPerson(person, countryList));

      if (onDataLoaded) {
        onDataLoaded(list);
      }

      return randomUUID(list);
    },
    enabled: !!countryList.length,
    retry: false,
    throwOnError: (error) => checkException(error, true),
  });
};
